/**
 * Shared JobPosting (schema.org) JSON-LD builder — GitHub issue #301,
 * "[GEO] JobPosting schema markup on public job pages — free Google for
 * Jobs indexing".
 *
 * Single source of truth for turning a job + its hiring company into the
 * schema.org `JobPosting` structured-data object Google for Jobs reads.
 * Deliberately framework/runtime-agnostic (no React, no DOM, no
 * Supabase) so it can be:
 *
 *  - called server-side by `apps/workers/src/api/publicJobs.ts` to decide
 *    whether a job's markup should exist at all (archived -> `null`), and
 *  - called client-side by the public job page
 *    (`apps/web/src/app/routes/PublicJobPage.tsx`) to render the
 *    `<script type="application/ld+json">` tag, and
 *  - reused unchanged by the Phase 5 SSG rebuild (#282) to emit the same
 *    markup at build time instead of client-render time — see #301's
 *    scope note: "schema templates written now should be reusable."
 *
 * Employment type / salary caveat: `jobs` (`db/schema.ts`) has no
 * `employment_type` or salary columns as of this issue (no migration
 * exists for them) — every job is emitted as `FULL_TIME` and `baseSalary`
 * is only included when a caller explicitly supplies one (always
 * `undefined`/`null` from the current data layer,
 * `apps/workers/src/api/publicJobs.ts`). TODO(#301 follow-up): add
 * `employment_type`/`salary_min`/`salary_max`/`salary_currency`/
 * `salary_unit` columns + a migration once product defines them, then
 * thread real values through instead of the `FULL_TIME` default below.
 */
/** Location strings that mean "no location constraint" / fully remote —
 * mirrors `apps/modal/src/settings.py`'s `PRESCREEN_REMOTE_LOCATION_TERMS`
 * ("remote,anywhere,worldwide,global") so "is this job remote?" answers
 * the same way in the sourcing pipeline and on the public job page. */
const REMOTE_LOCATION_TERMS = new Set(['remote', 'anywhere', 'worldwide', 'global']);
export function isRemoteLocation(location) {
    if (location == null)
        return false;
    return REMOTE_LOCATION_TERMS.has(location.trim().toLowerCase());
}
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Converts a plain-text job description into minimal, safe HTML — Google
 * strongly prefers an HTML-formatted `description` (ticket scope list).
 * Paragraphs are split on blank lines; single newlines within a paragraph
 * become `<br>`.
 */
export function toHtmlDescription(text) {
    const paragraphs = text
        .split(/\n{2,}/)
        .map((para) => para.trim())
        .filter((para) => para.length > 0);
    if (paragraphs.length === 0)
        return '';
    return paragraphs.map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`).join('');
}
/**
 * Builds the schema.org `JobPosting` JSON-LD object for a single job.
 *
 * Returns `null` for archived jobs (`isArchived: true`) — Google must
 * never be served stale-posting markup for a job whose public page has
 * been removed/410'd (GH issue #246 "archived-jobs class"; `jobs.status
 * === 'CLOSED'` is this app's archive state, see
 * `apps/workers/src/api/jobs.ts`'s `updateJob` doc-comment).
 */
export function buildJobPostingJsonLd(job) {
    if (job.isArchived)
        return null;
    const description = job.descriptionIsHtml ? job.description : toHtmlDescription(job.description);
    const remote = isRemoteLocation(job.location);
    const schema = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        title: job.title,
        description,
        datePosted: job.datePosted,
        validThrough: job.validThrough,
        employmentType: job.employmentType ?? 'FULL_TIME',
        hiringOrganization: {
            '@type': 'Organization',
            name: job.hiringOrganizationName,
        },
        directApply: true,
        url: job.jobPageUrl,
    };
    if (remote) {
        schema.jobLocationType = 'TELECOMMUTE';
        schema.applicantLocationRequirements = {
            '@type': 'Country',
            name: 'US',
        };
    }
    else if (job.location) {
        schema.jobLocation = {
            '@type': 'Place',
            address: {
                '@type': 'PostalAddress',
                addressLocality: job.location,
            },
        };
    }
    if (job.baseSalary) {
        schema.baseSalary = {
            '@type': 'MonetaryAmount',
            currency: job.baseSalary.currency,
            value: {
                '@type': 'QuantitativeValue',
                minValue: job.baseSalary.minValue,
                maxValue: job.baseSalary.maxValue,
                value: job.baseSalary.value,
                unitText: job.baseSalary.unitText,
            },
        };
    }
    return schema;
}
/**
 * Rolling `validThrough` window off `datePosted` (days), floored at
 * `now + MIN_VALID_THROUGH_WINDOW_DAYS` — see `computeValidThrough`.
 */
export const DEFAULT_VALID_THROUGH_WINDOW_DAYS = 90;
export const MIN_VALID_THROUGH_WINDOW_DAYS = 30;
/**
 * Computes a `validThrough` timestamp guaranteed to be in the future
 * relative to `now` — Google penalizes stale postings whose
 * `validThrough` has already passed (ticket scope: "validThrough +
 * remove/410 expired jobs — Google penalizes stale postings").
 *
 * No explicit job-expiry column exists in `jobs` (`db/schema.ts`) today,
 * so this is a rolling window off `datePosted`
 * (`DEFAULT_VALID_THROUGH_WINDOW_DAYS`), floored at
 * `now + MIN_VALID_THROUGH_WINDOW_DAYS` so a posting created long ago but
 * still open never emits an already-past `validThrough`.
 */
export function computeValidThrough(datePosted, now = new Date()) {
    const fromPosting = new Date(datePosted.getTime());
    fromPosting.setUTCDate(fromPosting.getUTCDate() + DEFAULT_VALID_THROUGH_WINDOW_DAYS);
    const minFloor = new Date(now.getTime());
    minFloor.setUTCDate(minFloor.getUTCDate() + MIN_VALID_THROUGH_WINDOW_DAYS);
    return fromPosting.getTime() > minFloor.getTime() ? fromPosting : minFloor;
}
// ------------------------------------------------------------------------
// Timeline / offer-guarantee claim guard (same rule as GH issue #300/#572,
// "Meta tag cleanup on live site — remove timeline claims (offer-language
// risk) + SEO consistency"). #301 (this file) defined the first version
// of this list before #300 landed; #300/#572's own scrub
// (`apps/web/src/__tests__/**`) reuses and extends it here rather than
// defining a second, divergent list — do the same for any future ticket
// that needs this guard.
// ------------------------------------------------------------------------
/**
 * Lower-cased substrings that indicate a timeline/offer-guarantee claim
 * (e.g. "offer within 24 hours", "guaranteed interview", "same-day
 * hire", "hired candidate in 3 weeks or less", "weeks, not months").
 * Deliberately conservative/example-driven rather than an exhaustive NLP
 * check — enough to catch the obvious offer-language risk called out in
 * #300/#301/#572 without false-positiving on ordinary job-description
 * prose (e.g. does NOT ban the bare word "weeks" or "startups", both of
 * which are legitimate copy elsewhere on the site).
 */
export const TIMELINE_CLAIM_PHRASES = [
    '24 hours',
    '48 hours',
    '24-hour',
    '48-hour',
    'same day offer',
    'same-day offer',
    'same day hire',
    'same-day hire',
    'guaranteed offer',
    'guaranteed interview',
    'guaranteed job',
    'instant hire',
    'instant offer',
    'hired today',
    'offer today',
    'start immediately',
    'immediate start guaranteed',
    'within 1 week',
    'within one week',
    // GH issue #300/#572 — the two specific fill-timeline phrases Robert
    // flagged as an offer-language risk (we have no fill-timeline guarantee
    // in our offer, so nothing on the site may promise one), plus the
    // generalized patterns the ticket's QA negative case greps for.
    'hired candidate in 3 weeks or less',
    'weeks, not months',
    'weeks not months',
    '3 weeks or less',
    '3 weeks',
    'weeks or less',
];
/** Returns the first matching banned phrase found in `text`, or `null`. */
export function findTimelineClaim(text) {
    if (!text)
        return null;
    const lower = text.toLowerCase();
    for (const phrase of TIMELINE_CLAIM_PHRASES) {
        if (lower.includes(phrase))
            return phrase;
    }
    return null;
}
/** `true` if `text` contains a banned timeline/offer-guarantee claim. */
export function containsTimelineClaim(text) {
    return findTimelineClaim(text) !== null;
}
//# sourceMappingURL=jobPosting.js.map